import MediaImage from "@/components/platform/MediaImage";
import { Play, Pause, X, Music, ChevronDown, ChevronUp, ExternalLink, SkipBack, SkipForward } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useMediaPlayer, youTubeId } from "@/hooks/platform/useMediaPlayer";

const fmt = (s: number) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

/** Floating pop-up player shown whenever something is being listened to. */
const MiniPlayer = () => {
  const {
    track, isPlaying, isYouTube, isEmbed, progress, duration, minimized,
    queue, index, hasNext, hasPrev, toggle, seek, close, setMinimized, next, prev,
  } = useMediaPlayer();
  if (!track) return null;

  const vid = youTubeId(track.youtube);
  const external = isYouTube || isEmbed;

  return (
    <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[70] w-[min(92vw,26rem)] animate-in slide-in-from-bottom-4 duration-300">
      <div className="rounded-2xl border border-white/10 bg-black/85 backdrop-blur-2xl shadow-[0_20px_60px_-20px_rgba(0,0,0,1)] overflow-hidden">
        {isYouTube && vid && !minimized && (
          <div className="aspect-video w-full bg-black">
            <iframe
              key={vid}
              src={`https://www.youtube-nocookie.com/embed/${vid}?autoplay=1&rel=0`}
              title={track.title}
              allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className="w-full h-full border-0"
            />
          </div>
        )}
        {isEmbed && track.embedUrl && !minimized && (
          <iframe
            key={track.embedUrl}
            src={track.embedUrl}
            title={track.title}
            height={152}
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            className="w-full border-0 bg-black"
            loading="lazy"
          />
        )}

        <div className="flex items-center gap-2 p-3">
          {track.artwork ? (
            <MediaImage src={track.artwork} alt={track.title} className="w-11 h-11 rounded-lg object-cover" loading="lazy" />
          ) : (
            <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-purple-900/50 to-black flex items-center justify-center">
              <Music className="w-4 h-4 text-purple-300" />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white truncate">{track.title}</p>
            <p className="text-xs text-white/50 truncate">
              {track.artist || (isYouTube ? "YouTube Music" : "Now playing")}
              {queue.length > 1 && <span className="ml-1 text-white/30">· {index + 1}/{queue.length}</span>}
            </p>
            {!external && (
              <div className="mt-1.5 flex items-center gap-2">
                <span className="text-[10px] text-white/40 tabular-nums">{fmt(progress)}</span>
                <Slider value={[progress]} max={Math.max(duration, 1)} step={1} onValueChange={([v]) => seek(v)} className="flex-1" />
                <span className="text-[10px] text-white/40 tabular-nums">{fmt(duration)}</span>
              </div>
            )}
          </div>

          <Button size="icon" variant="ghost" className="h-8 w-8 text-white/70 hover:text-white disabled:opacity-30" onClick={prev} disabled={!hasPrev && external} aria-label="Previous track">
            <SkipBack className="w-4 h-4" />
          </Button>
          {!external && (
            <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full bg-white/10 text-white hover:bg-white/20" onClick={toggle} aria-label={isPlaying ? "Pause" : "Play"}>
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
          )}
          <Button size="icon" variant="ghost" className="h-8 w-8 text-white/70 hover:text-white disabled:opacity-30" onClick={next} disabled={!hasNext} aria-label="Next track">
            <SkipForward className="w-4 h-4" />
          </Button>
          {track.link && (
            <a href={track.link} target="_blank" rel="noreferrer" className="text-white/50 hover:text-white" aria-label="Open source">
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          {external && (
            <Button size="icon" variant="ghost" className="h-8 w-8 text-white/60 hover:text-white" onClick={() => setMinimized(!minimized)} aria-label={minimized ? "Expand player" : "Minimise player"}>
              {minimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          )}
          <Button size="icon" variant="ghost" className="h-8 w-8 text-white/60 hover:text-white" onClick={close} aria-label="Close player">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MiniPlayer;
